/**
 * 壊れた lazy チャンクからの自動復旧（PRODUCT_DEV-612 FB）。
 *
 * ## 何が起きていたか
 *
 * デプロイは CodePipeline の `S3DeployAction` で行われ、**古いチャンクは消していない**
 * （dev バケットには 7 月のチャンクも残っており 200 で JS を返す）。壊れる経路は削除では
 * なく **Service Worker の precache 汚染**である。
 *
 * 1. `/sw.js` は CloudFront の 60 秒キャッシュ圏にあり、invalidation を待たずに新しい
 *    `sw.js` が取得されうる。一方 `assets/*`（JS 150 本 + sourcemap 150 本）は
 *    まだアップロードの途中でありうる。
 * 2. この窓で SW が install すると、**まだ S3 に無いチャンク URL** に対して CloudFront が
 *    SPA フォールバック（403/404 → `/index.html`）で **200 + `text/html`** を返す。
 *    workbox は `response.ok` しか見ないので、それを正規の JS として precache に焼き込む。
 * 3. `skipWaiting` + `clientsClaim` により、その汚染 SW が即座に既存タブを掌握する。
 * 4. 以後そのチャンクの `import()` は毎回 HTML を掴んで
 *      Failed to load module script: Expected a JavaScript-or-Wasm module script
 *      but the server responded with a MIME type of "text/html"
 *    で失敗する。React Router のナビゲーションは `startTransition` に包まれているため、
 *    **URL だけ変わって画面が変わらない**（そのまま操作を続けると真っ白になる）。
 *
 * ## なぜ通常リロードで直らないか
 *
 * 汚染の実体は **Cache Storage**（SW の precache）にある。Cache Storage は
 * `Cache-Control` を無視し、通常リロードも SW を経由する。スーパーリロードだけが
 * 直るのは、それが SW をバイパスするからである。したがって復旧には
 * **キャッシュ削除 + SW の unregister** が要る。単なる `location.reload()` では足りない。
 *
 * 配信側の根本対策（`/assets/*` に SPA フォールバックを効かせない）は
 * `infra/unizo/cdk/lib/unizo/stacks/web-unizo-stack.ts` 側で入れている。
 * こちらは **既に汚染された端末を救うための後始末**。
 */
import { markChunkSettled, markChunkStart } from "@/lib/navDiagnostics";

/**
 * この時間内に `import()` が settle しなければ「ハングした」とみなす。
 *
 * 汚染された precache から HTML を掴んだ場合は reject するが、**settle せずに止まる**
 * 経路も現に存在する（例外も出ず Network にも残らないため、これを待ち続けると
 * ユーザは永久に固まる）。reject と同じ復旧に倒すためにタイムアウトを噛ませる。
 * 遅い回線でのチャンク取得を殺さない程度に長くとる。
 */
export const CHUNK_LOAD_TIMEOUT_MS = 8_000;

/** `import()` が時間内に settle しなかったことを表すエラー。 */
export class ChunkLoadTimeoutError extends Error {
  // パラメータプロパティは erasableSyntaxOnly で使えないため明示的に宣言する
  readonly chunkName: string;

  constructor(chunkName: string) {
    super(
      `route chunk did not settle in ${CHUNK_LOAD_TIMEOUT_MS}ms: ${chunkName}`,
    );
    this.name = "ChunkLoadTimeoutError";
    this.chunkName = chunkName;
  }
}

/**
 * ルートチャンクの `import()` を計測付き・タイムアウト付きで実行する。
 *
 * 進行中のチャンクは `navDiagnostics` に登録され、遷移が固まったときの報告に載る
 * （「固まった瞬間に読み込み途中のチャンクがあったか」が原因切り分けの決め手になる）。
 */
export function loadRouteChunk<T>(
  name: string,
  factory: () => Promise<T>,
): Promise<T> {
  markChunkStart(name);
  let timer = 0;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = window.setTimeout(
      () => reject(new ChunkLoadTimeoutError(name)),
      CHUNK_LOAD_TIMEOUT_MS,
    );
  });
  return Promise.race([factory(), timeout]).then(
    (mod) => {
      window.clearTimeout(timer);
      markChunkSettled(name, "loaded");
      return mod;
    },
    (err: unknown) => {
      window.clearTimeout(timer);
      markChunkSettled(
        name,
        err instanceof ChunkLoadTimeoutError ? "timeout" : "failed",
      );
      throw err;
    },
  );
}

/** リロード時刻の記録キー（sessionStorage）。 */
export const CHUNK_RELOAD_KEY = "chunk-reload-at";

/** この時間内の再失敗はリロードせず例外にする（無限リロード防止）。 */
export const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

/**
 * 復旧（キャッシュ削除 + SW unregister + リロード）を試みてよいか。
 *
 * 配信側が本当に壊れているときにリロードループへ入らないよう、直近の試行から
 * クールダウン経過後のみ許可する。
 */
export function shouldAttemptChunkRecovery(
  now: number,
  lastAttemptAt: number,
): boolean {
  if (!Number.isFinite(lastAttemptAt) || lastAttemptAt <= 0) return true;
  return now - lastAttemptAt >= CHUNK_RELOAD_COOLDOWN_MS;
}

/** sessionStorage から前回の試行時刻を読む（使えない環境では 0）。 */
export function readLastRecoveryAt(): number {
  try {
    return Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
  } catch {
    // プライベートモード等で sessionStorage が使えない場合はクールダウン無しで進む
    return 0;
  }
}

/** sessionStorage に今回の試行時刻を書く（失敗しても復旧は続行する）。 */
export function writeLastRecoveryAt(now: number): void {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    // 記録できなくてもリロードは試す（次回クールダウンが効かないだけ）
  }
}

/**
 * push 通知用 SW のスコープ。`usePushNotification` が
 * `${import.meta.env.BASE_URL}push/` に登録している。
 * **これを unregister すると push 購読が壊れる**ので復旧対象から除外する。
 */
const PUSH_SW_SCOPE_SUFFIX = "/push/";

/**
 * Cache Storage を全消しし、メインの SW を unregister してからリロードする。
 *
 * どちらの後始末も「できなければ諦めて次へ進む」。最悪でもリロードまでは到達させる
 * （リロードすれば少なくとも新しい `index.html` は取り直せる）。
 */
export async function recoverFromBrokenChunk(): Promise<void> {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch (e) {
    console.warn("[chunk] failed to clear caches", e);
  }

  try {
    const registrations =
      (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
    await Promise.all(
      registrations
        .filter((r) => !r.scope.endsWith(PUSH_SW_SCOPE_SUFFIX))
        .map((r) => r.unregister()),
    );
  } catch (e) {
    console.warn("[chunk] failed to unregister service worker", e);
  }

  window.location.reload();
}
